// src/services/adminUsersService.ts
//
// Client-side wrapper around the `admin-users` Supabase Edge Function.
//
// Replaces direct `supabase.auth.admin.*` calls (issue #17). The browser
// is built with the anon key, which has no permission to call the
// auth.admin namespace — those calls silently failed in production. The
// edge function holds the service-role key and verifies the caller is
// an active admin via the JWT.

import { supabase } from '../supabase/config';

interface InviteOptions {
  email: string;
  userMetadata?: Record<string, unknown>;
}

interface CreateOptions {
  email: string;
  password?: string;
  emailConfirm?: boolean;
  userMetadata?: Record<string, unknown>;
}

interface MinimalAuthUser {
  id: string;
  email: string | null;
  confirmed_at: string | null;
  last_sign_in_at: string | null;
  user_metadata: Record<string, unknown> | null;
}

async function callAdminUsers<T>(body: Record<string, unknown>): Promise<T> {
  // supabase.functions.invoke automatically attaches the user's bearer
  // token from the active session, so the edge function can verify
  // caller identity.
  const { data, error } = await supabase.functions.invoke('admin-users', {
    body,
  });
  if (error) throw error;
  return data as T;
}

export const adminUsersService = {
  async invite(opts: InviteOptions): Promise<MinimalAuthUser | null> {
    const res = await callAdminUsers<{ user: MinimalAuthUser | null }>({
      action: 'invite',
      email: opts.email,
      userMetadata: opts.userMetadata,
    });
    return res.user;
  },

  async create(opts: CreateOptions): Promise<MinimalAuthUser | null> {
    const res = await callAdminUsers<{ user: MinimalAuthUser | null }>({
      action: 'create',
      email: opts.email,
      password: opts.password,
      emailConfirm: opts.emailConfirm,
      userMetadata: opts.userMetadata,
    });
    return res.user;
  },

  async delete(userId: string): Promise<void> {
    await callAdminUsers<{ success: true }>({
      action: 'delete',
      userId,
    });
  },

  async list(): Promise<MinimalAuthUser[]> {
    const res = await callAdminUsers<{ users: MinimalAuthUser[] }>({
      action: 'list',
    });
    return res.users;
  },
};
